import { objectSpread2 as _objectSpread2 } from './_virtual/_rollupPluginBabelHelpers.mjs';
import { isSubmitter, isGlobalInstance } from './dom.mjs';
import { isPlainObject, stripFiles, deepEqual } from './util.mjs';
import { formatIssues } from './standard-schema.mjs';

var DEFAULT_INTENT_NAME = '__INTENT__';

/**
 * Construct a form data with the submitter value.
 * It utilizes the submitter argument on the FormData constructor from modern browsers
 * with fallback to append the submitter value in case it is not unsupported.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FormData/FormData#parameters
 */
function getFormData(form, submitter) {
  var payload = new FormData(form, submitter);
  if (submitter) {
    if (!isSubmitter(submitter)) {
      throw new TypeError('The submitter must be an input or button element with type submit.');
    }
    if (submitter.name) {
      var entries = payload.getAll(submitter.name);

      // This assumes the submitter value to be always unique, which should be fine in most cases
      if (!entries.includes(submitter.value)) {
        payload.append(submitter.name, submitter.value);
      }
    }
  }
  return payload;
}

/**
 * Convert a string path into an array of segments.
 *
 * @example
 * ```js
 * getPathSegments("object.key");       // → ['object', 'key']
 * getPathSegments("array[0].content"); // → ['array', 0, 'content']
 * getPathSegments("todos[]");          // → ['todos', '']
 * ```
 */
function getPathSegments(path) {
  if (!path) return [];
  var tokenRegex = /([^.[\]]+)|\[(\d*)\]/g;
  var segments = [];
  var lastIndex = 0,
    match;
  while (match = tokenRegex.exec(path)) {
    // allow a single “.” between tokens
    if (match.index !== lastIndex) {
      if (!(match.index === lastIndex + 1 && path[lastIndex] === '.')) {
        throw new Error("Invalid path syntax at position ".concat(lastIndex, " in \"").concat(path, "\""));
      }
    }
    var [, key, index] = match;
    if (key !== undefined) {
      if (key === '__proto__' || key === 'constructor') {
        throw new Error("Invalid path segment \"".concat(key, "\""));
      }
      segments.push(key);
    } else if (index === '') {
      segments.push('');
    } else {
      var number = Number(index);
      if (!Number.isInteger(number) || number < 0) {
        throw new Error("Invalid path segment: array index must be a non-negative integer, got ".concat(number));
      }
      segments.push(number);
    }
    lastIndex = tokenRegex.lastIndex;
  }
  if (lastIndex !== path.length) {
    throw new Error("Invalid path syntax at position ".concat(lastIndex, " in \"").concat(path, "\""));
  }
  return segments;
}

/**
 * Returns a formatted name from the path segments based on the dot and bracket notation.
 *
 * @example
 * ```js
 * formatPathSegments(['object', 'key']); // → "object.key"
 * formatPathSegments(['array', 0, 'content']); // → "array[0].content"
 * formatPathSegments(['todos', '']); // → "todos[]"
 * ```
 */
function formatPathSegments(segments) {
  return segments.reduce((path, segment) => appendPathSegment(path, segment), '');
}

/**
 * Append one more segment onto an existing path string.
 *
 * - segment = `undefined`  ⇒ no-op
 * - segment = `""`         ⇒ empty brackets "[]"
 * - segment = `number`     ⇒ bracket notation "[n]"
 * - segment = `string`     ⇒ dot-notation ".prop"
 */
function appendPathSegment(path, segment) {
  // 1) nothing to append
  if (typeof segment === 'undefined') {
    return path !== null && path !== void 0 ? path : '';
  }

  // 2) explicit empty-segment => empty bracket
  if (segment === '') {
    // even as first segment, "[]" is valid
    return "".concat(path, "[]");
  }

  // 3) numeric index => [n]
  if (typeof segment === 'number') {
    return "".concat(path, "[").concat(segment, "]");
  }

  // 4) non-empty string => .prop (no leading dot if no base)
  return path ? "".concat(path, ".").concat(segment) : segment;
}

/**
 * Returns true if `prefix` is a valid leading path of `name`.
 *
 * @example
 * ```js
 * isPrefix("foo.bar.baz", "foo.bar")        // → true
 * isPrefix("foo.bar[3].baz", "foo.bar[3]")  // → true
 * isPrefix("foo.bar[3].baz", "foo.bar")     // → true
 * isPrefix("foo.bar[3].baz", "foo.baz")     // → false
 * isPrefix("foo", "foo.bar")                // → false
 * ```
 */
function isPrefix(name, prefix) {
  return getRelativePath(name, getPathSegments(prefix)) !== null;
}

/**
 * Return the segments of `fullPathStr` that come after the `baseSegments` prefix.
 *
 * @param fullPathStr     Full path as a dot/bracket string
 * @param basePath    Base path, already parsed into segments
 * @returns               The “tail” segments, or `null` if `fullPathStr` isn’t nested under `baseSegments`
 *
 * @example
 * ```js
 * getRelativePath("foo.bar[0].qux", ["foo","bar"])  // → [0, "qux"]
 * getRelativePath("a.b.c.d", ["a","b"])             // → ["c","d"]
 * getRelativePath("foo", ["foo","bar"])             // → null
 * ```
 */
function getRelativePath(name, basePath) {
  var fullPath = getPathSegments(name);

  // if full is at least as long *and* starts with the base…
  if (fullPath.length >= basePath.length && basePath.every((segment, i) => segment === fullPath[i])) {
    return fullPath.slice(basePath.length);
  }
  return null;
}

/**
 * Assign a value to a target object by following the path segments.
 */
function setValueAtPath(target, pathOrSegments, valueOrFn) {
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  try {
    // 1) normalize + validate path
    var segments = typeof pathOrSegments === 'string' ? getPathSegments(pathOrSegments) : pathOrSegments;
    if (segments.length === 0) {
      throw new Error('Cannot set value at the object root');
    }
    if (segments.some((segment, i) => segment === '' && i < segments.length - 1)) {
      throw new Error("Empty brackets '[]' only allowed at end of path (\"".concat(pathOrSegments, "\")"));
    }

    // 2) clone root if needed
    var result = options.clone ? _objectSpread2({}, target) : target;
    var pointer = result;

    // 3) drill down, cloning ancestors
    for (var i = 0; i < segments.length - 1; i++) {
      var currentSegment = segments[i];
      var nextSegment = segments[i + 1];
      var child = pointer[currentSegment];
      if (Array.isArray(child)) {
        child = options.clone ? child.slice() : child;
      } else if (isPlainObject(child)) {
        child = options.clone ? _objectSpread2({}, child) : child;
      } else {
        child = typeof nextSegment === 'number' || nextSegment === '' ? [] : {};
      }
      pointer[currentSegment] = child;
      pointer = child;
    }

    // 4) final set or push
    var last = segments[segments.length - 1];
    var oldValue = pointer[last];
    var newValue = typeof valueOrFn === 'function' ? valueOrFn(oldValue) : valueOrFn;
    if (last === '') {
      if (!Array.isArray(pointer)) {
        throw new Error("Cannot push to non-array at \"".concat(pathOrSegments, "\""));
      }
      pointer.push(newValue);
    } else {
      pointer[last] = newValue;
    }
    return result;
  } catch (err) {
    if (options !== null && options !== void 0 && options.silent) {
      return target;
    }
    throw err;
  }
}

/**
 * Retrive the value from a target object by following the path segments.
 */
function getValueAtPath(target, pathOrSegments) {
  var pointer = target;
  var segments = typeof pathOrSegments === 'string' ? getPathSegments(pathOrSegments) : pathOrSegments;
  for (var segment of segments) {
    if (segment === '') {
      throw new Error("Cannot access empty segment \"[]\" in \"".concat(pathOrSegments, "\""));
    }
    if (pointer == null || !Object.prototype.hasOwnProperty.call(pointer, segment)) {
      return undefined;
    }
    pointer = pointer[segment];
  }
  return pointer;
}

/**
 * Parse `FormData` or `URLSearchParams` into a submission object.
 * This function structures the form values based on the naming convention.
 * It also includes all the field names and extracts the intent from the submission.
 *
 * @see https://conform.guide/api/react/future/parseSubmission
 * @example
 * ```ts
 * const formData = new FormData();
 *
 * formData.append('email', 'test@example.com');
 * formData.append('password', 'secret');
 *
 * parseSubmission(formData)
 * // {
 * //   payload: { email: 'test@example.com', password: 'secret' },
 * //   fields: ['email', 'password'],
 * //   intent: null,
 * // }
 *
 * // If you have an intent field
 * formData.append('intent', 'login');
 * parseSubmission(formData, { intentName: 'intent' })
 * // {
 * //   payload: { email: 'test@example.com', password: 'secret' },
 * //   fields: ['email', 'password'],
 * //   intent: 'login',
 * // }
 * ```
 */
function parseSubmission(formData, options) {
  var _options$intentName;
  var intentName = (_options$intentName = options === null || options === void 0 ? void 0 : options.intentName) !== null && _options$intentName !== void 0 ? _options$intentName : DEFAULT_INTENT_NAME;
  var submission = {
    payload: {},
    fields: [],
    intent: null
  };
  for (var _name of new Set(formData.keys())) {
    var _options$skipEntry;
    if (_name !== intentName && !(options !== null && options !== void 0 && (_options$skipEntry = options.skipEntry) !== null && _options$skipEntry !== void 0 && _options$skipEntry.call(options, _name))) {
      var _value = formData.getAll(_name);
      setValueAtPath(submission.payload, _name, _value.length > 1 ? _value : _value[0], {
        silent: true // Avoid errors if the path is invalid
      });
      submission.fields.push(_name);
    }
  }
  if (intentName) {
    // We take the first value of the intent field if it exists.
    var intent = formData.get(intentName);
    if (typeof intent === 'string') {
      submission.intent = intent;
    }
  }
  return submission;
}

/**
 * Creates a SubmissionResult object from a submission, adding validation results and intended values.
 * This function will remove all files in the submission payload by default since
 * file inputs cannot be initialized with files.
 * You can specify `keepFiles: true` to keep the files if needed.
 *
 * @see https://conform.guide/api/react/future/report
 * @example
 * ```ts
 * // Report the submission with the field errors
 * report(submission, {
 *  error: {
 *    fieldErrors: {
 *      email: ['Invalid email format'],
 *      password: ['Password is required'],
 *    },
 * })
 *
 * // Report the submission with a form error
 * report(submission, {
 *   error: {
 *     formErrors: ['Invalid credentials'],
 *   },
 * })
 *
 * // Reset the form
 * report(submission, {
 *   reset: true,
 * })
 * ```
 */

function report(submission) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var error;
  if (options.error == null) {
    error = options.error;
  } else {
    var _options$error$issues;
    error = formatIssues((_options$error$issues = options.error.issues) !== null && _options$error$issues !== void 0 ? _options$error$issues : []);
    if (options.error.formErrors) {
      error.formErrors.push(...options.error.formErrors);
    }
    if (options.error.fieldErrors) {
      for (var [_name2, messages] of Object.entries(options.error.fieldErrors)) {
        if (messages.length === 0) {
          continue;
        }
        if (!error.fieldErrors[_name2]) {
          error.fieldErrors[_name2] = messages;
        } else {
          error.fieldErrors[_name2].push(...messages);
        }
      }
    }
  }
  var intendedValue = options.reset ? null : typeof options.intendedValue === 'undefined' || submission.payload === options.intendedValue ? undefined : options.intendedValue && !options.keepFiles ? stripFiles(options.intendedValue) : options.intendedValue;
  if (options.hideFields) {
    for (var _name3 of options.hideFields) {
      var path = getPathSegments(_name3);
      setValueAtPath(submission.payload, path, undefined);
      if (intendedValue) {
        setValueAtPath(intendedValue, path, undefined);
      }
    }
  }
  return {
    submission: options.keepFiles ? submission : _objectSpread2(_objectSpread2({}, submission), {}, {
      payload: stripFiles(submission.payload)
    }),
    intendedValue,
    error
  };
}

/**
 * A utility function that checks whether the current form data differs from the default values.
 *
 * @see https://conform.guide/api/react/future/isDirty
 * @example Enable a submit button only if the form is dirty
 *
 * ```tsx
 * const dirty = useFormData(
 *   formRef,
 *   (formData) => isDirty(formData, { defaultValue }) ?? false,
 * );
 *
 * return (
 *   <button type="submit" disabled={!dirty}>
 *     Save changes
 *   </button>
 * );
 * ```
 */
function isDirty(
/**
 * The current form data to compare. It can be:
 *
 * - A `FormData` object
 * - A `URLSearchParams` object
 * - A plain object that was parsed from form data (i.e. `submission.payload`)
 */
formData, options) {
  if (!formData) {
    return;
  }
  var formValue = formData instanceof FormData || formData instanceof URLSearchParams ? parseSubmission(formData, {
    intentName: options === null || options === void 0 ? void 0 : options.intentName,
    skipEntry: options === null || options === void 0 ? void 0 : options.skipEntry
  }).payload : formData;
  var defaultValue = options === null || options === void 0 ? void 0 : options.defaultValue;
  var serializeData = value => {
    if (options !== null && options !== void 0 && options.serialize) {
      return options.serialize(value, serialize);
    }
    return serialize(value);
  };
  function normalize(data) {
    var value = serializeData(data);
    if (typeof value === 'undefined') {
      value = data;
    }

    // Removes empty strings, so that both empty string, empty file, null and undefined are treated as the same
    if (value === '' || value === null) {
      return undefined;
    }
    if (isGlobalInstance(value, 'File')) {
      // Remove empty File as well, which happens if no File was selected
      if (value.name === '' && value.size === 0) {
        return undefined;
      }

      // If the value is a File, no need to serialize it
      return value;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return undefined;
      }
      var array = value.map(normalize);
      if (array.length === 1 && (typeof array[0] === 'string' || array[0] === undefined)) {
        return array[0];
      }
      return array;
    }
    if (isPlainObject(value)) {
      var entries = Object.entries(value).reduce((list, _ref) => {
        var [key, value] = _ref;
        var normalizedValue = normalize(value);
        if (typeof normalizedValue !== 'undefined') {
          list.push([key, normalizedValue]);
        }
        return list;
      }, []);
      if (entries.length === 0) {
        return undefined;
      }
      return Object.fromEntries(entries);
    }
    return value;
  }
  return !deepEqual(normalize(formValue), normalize(defaultValue));
}

/**
 * Convert an unknown value into something acceptable for HTML form submission.
 * Returns `undefined` when the value cannot be represented in form data.
 *
 * Input -> Output:
 * - string -> string
 * - null -> '' (empty string)
 * - boolean -> 'on' | '' (checked semantics)
 * - number | bigint -> value.toString()
 * - Date -> value.toISOString()
 * - File -> File
 * - FileList -> File[]
 * - Array -> string[] or File[] if all items serialize to the same kind; otherwise undefined
 * - anything else -> undefined
 */
function serialize(value) {
  function serializePrimitive(value) {
    if (typeof value === 'string' || value === null) {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'on' : '';
    }
    if (typeof value === 'number' || typeof value === 'bigint') {
      return value.toString();
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (isGlobalInstance(value, 'File')) {
      return value;
    }
  }
  if (Array.isArray(value)) {
    var _options = [];
    var files = [];
    for (var item of value) {
      var serialized = serializePrimitive(item);
      if (typeof serialized === 'undefined') {
        return;
      }

      // It is unclear what `null` in a file array should mean, so we treat it as a string instead
      if (typeof serialized === 'string' || serialized === null) {
        if (files.length > 0) {
          return;
        }
        _options.push(serialized !== null && serialized !== void 0 ? serialized : '');
      } else {
        if (_options.length > 0) {
          return;
        }
        files.push(serialized);
      }
    }
    if (_options.length === value.length) {
      return _options;
    }
    if (files.length === value.length) {
      return files;
    }

    // If not all items are strings or files, return nothing
  }
  if (isGlobalInstance(value, 'FileList')) {
    return Array.from(value);
  }
  return serializePrimitive(value);
}

export { DEFAULT_INTENT_NAME, appendPathSegment, formatPathSegments, getFormData, getPathSegments, getRelativePath, getValueAtPath, isDirty, isPrefix, parseSubmission, report, serialize, setValueAtPath };
